// src/components/home/FeaturedTripBanner.jsx
// Banner for the showcased trips (FEATURES.CURATED_SHOWCASE: the Kashmir trip and the Hornbill Festival),
// taking turns every few seconds (paused while hovered or focused; no auto turn for reduced motion).
//   <FeaturedTripBanner />                 big photo card (below the search box)
//   <FeaturedTripBanner variant="strip" /> slim strip at the top of the hero, seen without scrolling
// src tags the click (?src=) for analytics. Used on the home page and the How to Reach pages.
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarDays, ShieldCheck } from "lucide-react";
import { SHOWCASE_ONLY, useShowcaseTrips } from "../../curated/showcase";

const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const TURN_MS = 5000;

// One shape for both kinds: a DMC trip (days, operator) or a festival page (dates, many operators)
const view = (t) => {
    const towns = (t.title.split(":")[1] || "").trim();
    return {
        ...t, towns,
        top: t.festival ? `${t.name}, ${t.when}` : `${t.name}, ${t.days} days`,
        when: t.festival ? "1 to 10 December 2026" : `${t.days} days, ${Math.max(1, t.days - 1)} nights`,
        by: t.festival ? t.subtitle.split(",")[0] : t.partner ? `by ${t.partner}` : "",
        unit: t.festival ? "" : "per person",
        cta: t.festival ? "See packages" : "View the trip",
        pill: t.festival ? "Festival" : "Featured trip",
    };
};

function useTurns(n) {
    const [i, setI] = useState(0);
    const paused = useRef(false);
    useEffect(() => {
        if (n < 2) return undefined;
        let reduce = false;
        try { reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { /* old browser */ }
        if (reduce) return undefined;
        const t = setInterval(() => { if (!paused.current && !document.hidden) setI((x) => (x + 1) % n); }, TURN_MS);
        return () => clearInterval(t);
    }, [n]);
    const hold = { onMouseEnter: () => { paused.current = true; }, onMouseLeave: () => { paused.current = false; }, onFocus: () => { paused.current = true; }, onBlur: () => { paused.current = false; } };
    return [Math.min(i, Math.max(0, n - 1)), setI, hold];
}

function Dots({ n, i, setI, light = false }) {
    if (n < 2) return null;
    return (
        <span className={`rc-ft-dots${light ? " light" : ""}`} role="group" aria-label="Featured trips">
            {Array.from({ length: n }, (_, k) => (
                <button key={k} type="button" aria-label={`Show featured trip ${k + 1}`} aria-pressed={k === i}
                    onClick={(e) => { e.preventDefault(); setI(k); }} className={k === i ? "on" : ""} />
            ))}
        </span>
    );
}

export default function FeaturedTripBanner({ style = null, variant = "card", src = "" }) {
    const cards = useShowcaseTrips();
    const [i, setI, hold] = useTurns(cards.length);
    if (!SHOWCASE_ONLY || !cards.length) return null;
    const t = view(cards[i]);
    const tagged = (fallback) => `${t.path}${t.path.includes("?") ? "&" : "?"}src=${src || fallback}`;
    if (variant === "strip") {
        return (
            <div className="rc-fts-wrap" style={style} {...hold}>
                <style>{CSS}</style>
                <Link key={t.key} to={tagged("home_strip")} className="rc-fts rc-ft-in" aria-label={`Featured: ${t.title}`}>
                    {t.cover && <span className="rc-fts-img" style={{ backgroundImage: `url(${t.cover})` }} aria-hidden="true" />}
                    <span className="rc-fts-text">
                        <span className="rc-fts-top"><span className="rc-fts-pill">New</span> {t.top}{t.from_pp > 0 ? <span className="rc-fts-price"> · from {inr(t.from_pp)}</span> : null}</span>
                        <span className="rc-fts-sub rc-fts-wide">{t.towns || t.subtitle}{t.by ? ` · ${t.by}` : ""}</span>
                        <span className="rc-fts-sub rc-fts-narrow">{t.from_pp > 0 ? <>from <b style={{ color: "#FF4E01" }}>{inr(t.from_pp)}</b>{t.unit ? " pp" : ""}</> : t.towns}{t.by ? ` · ${t.by}` : ""}</span>
                    </span>
                    <span className="rc-fts-cta">{t.festival ? "See packages" : "View trip"} <ArrowRight size={15} /></span>
                </Link>
                <Dots n={cards.length} i={i} setI={setI} />
            </div>
        );
    }
    return (
        <section aria-label="Featured trips" className="rc-ftb" style={{ maxWidth: 900, width: "calc(100% - 32px)", margin: "28px auto 0", position: "relative", zIndex: 5, textAlign: "left", ...style }} {...hold}>
            <style>{CSS}</style>
            <Link key={t.key} to={tagged("home_banner")} className="rc-ftb-card rc-ft-in" style={{ "--img": t.cover ? `url(${t.cover})` : t.gradient || "#0E7490" }}>
                <div className="rc-ftb-body">
                    <span className="rc-ftb-pill">{t.pill}</span>
                    <div className="rc-ftb-title">{t.name}</div>
                    {t.towns && <div className="rc-ftb-towns"><MapPin size={14} /> {t.towns}</div>}
                    <div className="rc-ftb-meta">
                        <span><CalendarDays size={14} /> {t.when}</span>
                        {t.by && <span><ShieldCheck size={14} /> {t.by}</span>}
                    </div>
                    <div className="rc-ftb-foot">
                        {t.from_pp > 0 && <span className="rc-ftb-price"><small>from</small> {inr(t.from_pp)} {t.unit && <small>{t.unit}</small>}</span>}
                        <span className="rc-ftb-cta">{t.cta} <ArrowRight size={16} /></span>
                    </div>
                </div>
            </Link>
            <span style={{ position: "absolute", right: 18, top: 22 }}><Dots n={cards.length} i={i} setI={setI} light /></span>
        </section>
    );
}

const CSS = `
.rc-fts-wrap{max-width:640px;width:calc(100% - 32px);margin:0 auto 14px;position:relative;z-index:10;text-align:left}
.rc-fts{display:flex;align-items:center;gap:12px;padding:7px 8px 7px 7px;border-radius:14px;background:#fff;border:1px solid #FFD8C7;
  box-shadow:0 8px 22px -14px rgba(255,78,1,.55);text-decoration:none;color:#0F172A;transition:border-color .15s, transform .15s}
.rc-fts:hover{border-color:#FF4E01;transform:translateY(-1px)}
.rc-fts-img{flex-shrink:0;width:64px;height:48px;border-radius:10px;background-size:cover;background-position:center 70%}
.rc-fts-text{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px}
.rc-fts-top{font-size:14.5px;font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.rc-fts-pill{display:inline-block;font-size:10px;font-weight:800;letter-spacing:.5px;text-transform:uppercase;background:#FF4E01;color:#fff;padding:2px 7px;border-radius:99px;margin-right:4px;vertical-align:2px}
.rc-fts-price{color:#FF4E01}
.rc-fts-sub{font-size:12.5px;color:#64748B;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.rc-fts-cta{flex-shrink:0;display:inline-flex;align-items:center;gap:6px;background:#FF4E01;color:#fff;font-weight:800;font-size:13.5px;padding:9px 13px;border-radius:10px}
.rc-fts-narrow{display:none}
.rc-ft-in{animation:rcFtIn .45s ease}
@keyframes rcFtIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion: reduce){.rc-ft-in{animation:none}}
.rc-ft-dots{display:flex;justify-content:center;gap:6px;margin-top:7px}
.rc-ft-dots button{width:7px;height:7px;padding:0;border:none;border-radius:99px;background:#E2CFC4;cursor:pointer;transition:width .25s, background .25s}
.rc-ft-dots button.on{width:20px;background:#FF4E01}
.rc-ft-dots.light{margin:0}
.rc-ft-dots.light button{background:rgba(255,255,255,.55)}
.rc-ft-dots.light button.on{background:#fff}
@media (max-width:480px){.rc-fts-wide,.rc-fts-price{display:none}.rc-fts-narrow{display:block}.rc-fts-cta{padding:9px 10px;font-size:0;gap:0}.rc-fts-cta svg{width:18px;height:18px}.rc-fts-img{width:52px;height:44px}}

.rc-ftb-card{display:block;min-height:230px;background-image:linear-gradient(90deg, rgba(6,18,32,.82) 0%, rgba(6,18,32,.6) 42%, rgba(6,18,32,.1) 78%), var(--img);border-radius:18px;overflow:hidden;background-size:cover;background-position:center 70%;
  text-decoration:none;color:#fff;box-shadow:0 18px 40px -22px rgba(15,23,42,.55);transition:transform .15s, box-shadow .15s}
.rc-ftb-card:hover{transform:translateY(-2px);box-shadow:0 24px 46px -22px rgba(15,23,42,.6)}
.rc-ftb-body{padding:24px 26px;max-width:520px;display:flex;flex-direction:column;gap:8px}
.rc-ftb-pill{align-self:flex-start;font-size:11px;font-weight:800;letter-spacing:.6px;text-transform:uppercase;background:#FF4E01;color:#fff;padding:4px 10px;border-radius:99px}
.rc-ftb-title{font-size:30px;font-weight:900;line-height:1.1;letter-spacing:-.5px;text-shadow:0 2px 10px rgba(0,0,0,.45)}
.rc-ftb-towns{display:flex;align-items:center;gap:6px;font-size:15px;font-weight:600;text-shadow:0 1px 3px rgba(0,0,0,.6)}
.rc-ftb-meta{display:flex;flex-wrap:wrap;gap:14px;font-size:13.5px;font-weight:600;opacity:.95}
.rc-ftb-meta span{display:inline-flex;align-items:center;gap:5px}
.rc-ftb-foot{display:flex;align-items:center;flex-wrap:wrap;gap:16px;margin-top:8px}
.rc-ftb-price{font-size:22px;font-weight:900}
.rc-ftb-price small{font-size:12.5px;font-weight:600;opacity:.9}
.rc-ftb-cta{display:inline-flex;align-items:center;gap:8px;background:#fff;color:#0F172A;font-weight:800;font-size:15px;padding:11px 18px;border-radius:12px}
.rc-ftb-card:hover .rc-ftb-cta{background:#FF4E01;color:#fff}
@media (max-width:640px){
  .rc-ftb-card{min-height:300px;background-image:linear-gradient(180deg, rgba(6,18,32,.15) 0%, rgba(6,18,32,.45) 40%, rgba(6,18,32,.88) 100%), var(--img)}
  .rc-ftb-body{padding:18px;justify-content:flex-end;min-height:300px;box-sizing:border-box}
  .rc-ftb-title{font-size:25px}
  .rc-ftb-cta{width:100%;justify-content:center}
}`;
